import type {
	MenuItemFlat,
	MenuMegaGroupNode,
	MenuMegaLinkNode,
	MenuMegaTabNode,
	MenuRootNode,
} from "./index";

const ROOT_KINDS = new Set(["link", "mega_tab"]);

function sortByOrder<T extends { sortOrder: number }>(rows: T[]): T[] {
	return [...rows].sort((a, b) => a.sortOrder - b.sortOrder);
}

/**
 * Theme Builder mega groups: left-to-right is `columnIndex`, then `sortOrder`
 * within that column. Menu PATCH resets `sortOrder` per column, so sorting by
 * `sortOrder` alone flips columns when two groups both have 0.
 */
export function compareMenuMegaGroups(
	a: { columnIndex: number; id?: string; sortOrder: number },
	b: { columnIndex: number; id?: string; sortOrder: number }
): number {
	if (a.columnIndex !== b.columnIndex) {
		return a.columnIndex - b.columnIndex;
	}
	if (a.sortOrder !== b.sortOrder) {
		return a.sortOrder - b.sortOrder;
	}
	if (a.id && b.id && a.id !== b.id) {
		return a.id.localeCompare(b.id);
	}
	return 0;
}

export function sortMenuMegaGroups<
	T extends { columnIndex: number; sortOrder: number },
>(groups: T[]): T[] {
	return [...groups].sort(compareMenuMegaGroups);
}

/** Normalize mega tab column count from stored tab.columnIndex (2–4) or group layout. */
export function resolveMegaTabColumnCount(
	stored: number | null | undefined,
	groups: { columnIndex: number }[]
): 2 | 3 | 4 {
	if (stored === 2 || stored === 3 || stored === 4) {
		return stored;
	}
	let maxIndex = 0;
	for (const g of groups) {
		if (g.columnIndex > maxIndex) {
			maxIndex = g.columnIndex;
		}
	}
	const derived = maxIndex + 1;
	if (derived <= 2) {
		return 2;
	}
	if (derived >= 4) {
		return 4;
	}
	return 3;
}

/** Empty / `#` stored href means no public category link. */
export function optionalMegaGroupHref(
	href: string | null | undefined
): string | undefined {
	const trimmed = href?.trim();
	if (!trimmed || trimmed === "#") {
		return undefined;
	}
	return trimmed;
}

function mapMegaLinks(linkRows: MenuItemFlat[]): MenuMegaLinkNode[] {
	return sortByOrder(
		linkRows
			.filter((l) => l.itemKind === "mega_link")
			.map((l) => ({
				id: l.id,
				label: l.label,
				href: l.href,
				openInNewTab: l.openInNewTab,
				sortOrder: l.sortOrder,
				description: l.description?.trim() || null,
			}))
	);
}

function buildMegaGroup(
	group: MenuItemFlat,
	linkRows: MenuItemFlat[]
): MenuMegaGroupNode {
	const href = optionalMegaGroupHref(group.href);
	return {
		id: group.id,
		label: group.label,
		columnIndex: group.columnIndex ?? 0,
		sortOrder: group.sortOrder,
		links: mapMegaLinks(linkRows),
		...(href ? { href } : {}),
	};
}

function buildMegaTab(
	root: MenuItemFlat,
	byParent: Map<string | null, MenuItemFlat[]>
): MenuMegaTabNode {
	const groupRows = byParent.get(root.id) ?? [];
	const groups: MenuMegaGroupNode[] = [];
	for (const group of groupRows) {
		if (group.itemKind !== "mega_group") {
			continue;
		}
		groups.push(buildMegaGroup(group, byParent.get(group.id) ?? []));
	}
	const sortedGroups = sortMenuMegaGroups(groups);
	return {
		id: root.id,
		label: root.label,
		href: root.href,
		openInNewTab: root.openInNewTab,
		sortOrder: root.sortOrder,
		dropdownStyle: "mega",
		itemKind: "mega_tab",
		megaMenuId: root.megaMenuId ?? null,
		columnCount: resolveMegaTabColumnCount(root.columnIndex, sortedGroups),
		groups: sortedGroups,
	};
}

/** Build nested roots from flat DB rows (shared by dashboard API and region resolve). */
export function buildMenuTreeFromFlat(items: MenuItemFlat[]): MenuRootNode[] {
	const byParent = new Map<string | null, MenuItemFlat[]>();
	for (const row of items) {
		const key = row.parentId;
		const list = byParent.get(key) ?? [];
		list.push(row);
		byParent.set(key, list);
	}
	for (const list of byParent.values()) {
		list.sort((a, b) => a.sortOrder - b.sortOrder);
	}

	const roots = byParent.get(null) ?? [];
	const result: MenuRootNode[] = [];

	for (const root of roots) {
		if (root.itemKind === "link") {
			result.push({
				id: root.id,
				label: root.label,
				href: root.href,
				openInNewTab: root.openInNewTab,
				sortOrder: root.sortOrder,
				itemKind: "link",
				dropdownStyle: "none",
			});
			continue;
		}
		if (root.itemKind !== "mega_tab" || !ROOT_KINDS.has(root.itemKind)) {
			continue;
		}
		result.push(buildMegaTab(root, byParent));
	}

	return sortByOrder(result);
}
